import { useState, useEffect } from 'react';
import { logger } from '../../utils/logger';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, RefreshCw, DollarSign, Play, Eye, Users, Music, ShoppingBag, Coins } from 'lucide-react';
import {
  StatCard,
  RevenuePieChart,
  TopTracksBarChart,
  TopContentLists,
  TokenMarketplaceStats,
  ContentOverview,
  RecentActivity
} from './analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API_URL}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      logger.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-20 text-gray-400">
        <p>Failed to load analytics data</p>
        <button 
          onClick={fetchAnalytics}
          className="mt-4 px-4 py-2 bg-purple-500 rounded-lg hover:bg-purple-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const { revenue, users, content, engagement, tokens, top_tracks, top_merch } = analytics;

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-400" />
            Fan Analytics Dashboard
          </h2>
          <p className="text-gray-400 mt-1">Track engagement and revenue metrics</p>
        </div>
        <button
          onClick={fetchAnalytics}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Revenue Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={`$${revenue.total.toLocaleString()}`}
          icon={DollarSign}
          color="green"
          subtitle="All-time earnings"
        />
        <StatCard
          title="Total Plays"
          value={engagement.total_plays.toLocaleString()}
          icon={Play}
          color="purple"
          subtitle="Track streams"
        />
        <StatCard
          title="Video Views"
          value={engagement.video_views.toLocaleString()}
          icon={Eye}
          color="cyan"
          subtitle="Total video views"
        />
        <StatCard
          title="Total Fans"
          value={users.total.toLocaleString()}
          icon={Users}
          color="pink"
          subtitle={`${users.premium} premium subscribers`}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Track Sales"
          value={`$${revenue.track_sales.toLocaleString()}`}
          icon={Music}
          color="purple"
        />
        <StatCard
          title="Merch Sales"
          value={`$${revenue.merch_sales.toLocaleString()}`}
          icon={ShoppingBag}
          color="pink"
        />
        <StatCard
          title="Token Sales"
          value={`$${revenue.token_sales.toLocaleString()}`}
          icon={Coins}
          color="yellow"
        />
        <StatCard
          title="Subscriptions"
          value={`$${revenue.subscriptions.toLocaleString()}`}
          icon={Users}
          color="green"
          subtitle={`${users.premium} active`}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RevenuePieChart data={revenue.breakdown} />
        <TopTracksBarChart tracks={top_tracks} />
      </div>

      {/* Top Content Lists */}
      <TopContentLists topTracks={top_tracks} topMerch={top_merch} />

      {/* Token & Marketplace Stats */}
      <TokenMarketplaceStats tokens={tokens} />

      {/* Content Overview */}
      <ContentOverview content={content} />

      {/* Recent Activity */}
      <RecentActivity users={users} engagement={engagement} />
    </div>
  );
}
